// « Programmer » et « Modifier » une séance quand le club a le planning des salles (D-135, maquettes p1 et p1b) : la séance
// se pose dans les créneaux du club. La liste « Quel créneau » : ceux de l'équipe, les créneaux libres (une demande part
// au responsable des salles, comme D-130), ceux libérés cette semaine, les créneaux en plus de l'équipe, puis « Ailleurs
// (hors salles du club) » (lieu en texte libre, séance « hors planning des salles », le responsable informé). On peut
// raccourcir dans le créneau, jamais déborder. Un coach sans créneau : les libres et « Ailleurs », avec « Demande un
// créneau au responsable des salles ». Un match amical à domicile prend un créneau ; les matchs officiels restent à part
// (la salle au choix, comme avant). Un club sans planning général (aucun créneau noté) garde le formulaire d'avant.
import './plannings.css';
import { activeSeason, createEvent, saveMatch, updateEvent, type ClubEvent, type MatchCompetition, type NewEvent, type Team } from '../lib/api';
import { answerSlotAsk, askSlotFor, hallBoard } from '../lib/salles-api';
import { WEEKDAYS, clock, hr } from '../domain/creneaux';
import { checkChoice, choiceHint, choicesFor, defaultChoice, hasPlan, nextDay, respFirstName, type Choice } from '../domain/plannings';
import { addDays, isoDay } from '../domain/salles';
import { localDate } from '../domain/schedule';
import { seasonEnd } from './club';
import type { Ctx } from './context';
import { $, $$, html, mount, openSheet, raw, toast, type Html } from './dom';
import { hallFieldHtml, wireHallField } from './salles-form';

const DAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const KINDS = { training: 'Entraînement', match: 'Match', other: 'Autre' } as const;
type Kind = keyof typeof KINDS;
const TITLE = { training: 'Entraînement', match: 'Match', other: 'Événement' } as const;

/** Une erreur de la base, dite simplement. */
function failText(e: unknown): string {
  const m = (e as Error)?.message ?? '';
  if (/hors des créneaux/.test(m)) return 'Hors des créneaux du club : choisis un créneau de la liste, ou « Ailleurs ».';
  if (/déjà la séance habituelle/.test(m)) return 'La séance habituelle de l’équipe est déjà dans ce créneau.';
  if (/salle du club/.test(m)) return 'C’est une salle du club : choisis un de ses créneaux.';
  if (/déjà en cours/.test(m)) return 'Une demande est déjà en cours pour ce créneau.';
  if (/plus libre/.test(m)) return 'Ce créneau n’est plus libre.';
  return 'Enregistrement impossible. Réessaie.';
}

const slotList = (list: Choice[], pick: string | null): Html => html`${list.map(c => html`<li><label class="ps-slot ${c.pending ? 'ps-pending' : ''}" ${c.kind === 'ailleurs' ? raw('data-guide="ps-ailleurs"') : ''}>
  <input type="radio" name="slot" value="${c.key}" ${c.key === pick ? raw('checked') : ''}>
  <span class="ps-txt"><strong>${c.title}</strong><span>${c.sub}</span></span></label></li>`)}`;

/** La phrase « Toutes les semaines · le mercredi, jusqu’à la fin de la saison ». */
const weeklyLine = (weekday: number | null) => weekday ? `· le ${WEEKDAYS[weekday - 1]}, jusqu’à la fin de la saison` : '· jusqu’à la fin de la saison';

/** Programmer, quand le club a le module « salles » ; sans planning général, `legacy` (le formulaire d'avant). */
export async function openSlotEventForm(ctx: Ctx, teams: Team[], onCreated: () => void, legacy: () => void): Promise<void> {
  const clubId = teams[0]!.club_id;
  const today = localDate(new Date());
  const b = await hallBoard(clubId, today, addDays(today, 13)).catch(() => null);
  if (!b || !hasPlan(b)) { legacy(); return; }
  let kind: Kind = 'training';
  let home = true;
  let teamId = teams[0]!.id;
  let list = choicesFor(b, teamId, new Date());
  let choice = defaultChoice(list);
  const squadsOf = (t: string) => ctx.squads.filter(q => q.team_id === t);

  openSheet('Programmer', html`
    <form class="form" id="f-ev" novalidate data-guide="ev-slot-form">
      <label class="field"><span>Équipe</span><select name="team">${teams.map(t => html`<option value="${t.id}">${t.name}</option>`)}</select></label>
      <div class="seg" role="radiogroup" aria-label="Type">
        ${(Object.keys(KINDS) as Kind[]).map((k, i) => html`<button type="button" role="radio" data-kind="${k}" aria-pressed="${String(i === 0)}">${KINDS[k]}</button>`)}
      </div>
      <label class="field ps-only" data-title hidden><span>Intitulé</span><input name="title" maxlength="120" placeholder="ex. Photo d’équipe"></label>
      <div class="match-fields form" hidden>
        <label class="field"><span>Adversaire</span><input name="opponent" maxlength="120" placeholder="ex. Valbrenne"></label>
        <div class="seg" role="radiogroup" aria-label="Lieu du match">
          <button type="button" role="radio" data-home="1" aria-pressed="true">À domicile</button>
          <button type="button" role="radio" data-home="0" aria-pressed="false">À l'extérieur</button>
        </div>
        <label class="field"><span>Compétition</span>
          <select name="competition"><option value="amical">Amical / préparation</option><option value="championnat">Championnat</option><option value="coupe">Coupe</option></select>
        </label>
        <label class="field squad-field" hidden><span>Équipe engagée</span><select name="squad"></select></label>
      </div>
      <div class="ps-only" data-slots>
        <p class="ps-sub">Quel créneau</p>
        <ul class="ps-slots" data-guide="ps-slots"></ul>
        <p class="hint" data-noslot hidden></p>
      </div>
      <div class="ps-only" data-hallsel hidden>${hallFieldHtml()}
        <p class="hint">Les matchs officiels arrivent tout seuls si l’équipe est reliée à la FFBB ; ils restent à part du planning des salles.</p></div>
      <label class="field ps-only" data-place hidden><span>Lieu</span><input name="place" maxlength="120" placeholder="ex. Parc de Rivebelle, footing"></label>
      <div class="row-3">
        <label class="field"><span data-date-label>À partir du</span><input name="date" type="date" required></label>
        <label class="field"><span>Début</span><input name="start" type="time" required></label>
        <label class="field"><span>Fin</span><input name="end" type="time"></label>
      </div>
      <p class="hint" data-hint></p>
      <label class="check" data-weekly><input type="checkbox" name="weekly"><span><strong>Toutes les semaines</strong> <span data-weekly-txt></span></span></label>
      <label class="field ps-only" data-msg hidden><span data-msg-label>Un mot</span><input name="msg" maxlength="300" placeholder="ex. Pour un groupe tir"></label>
      <label class="field"><span>Infos pour l’équipe (facultatif)</span><textarea name="note" rows="2" maxlength="500" placeholder="ex. RDV 14h15 pour le covoiturage"></textarea></label>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit" data-go>Programmer</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-ev')!;
    const msg = $(f, '.form-msg')!;
    const ul = $(f, '.ps-slots')!;
    const date = $<HTMLInputElement>(f, '[name=date]')!, start = $<HTMLInputElement>(f, '[name=start]')!, end = $<HTMLInputElement>(f, '[name=end]')!;
    const weekly = $<HTMLInputElement>(f, '[name=weekly]')!;
    const squadSel = $<HTMLSelectElement>(f, '[name=squad]')!;
    const comp = $<HTMLSelectElement>(f, '[name=competition]')!;
    const show = (sel: string, on: boolean) => { $(f, sel)!.hidden = !on; };
    // Le mode du formulaire : la liste des créneaux, la salle au choix (match officiel à domicile), le lieu (à l'extérieur).
    const mode = (): 'slots' | 'hall' | 'away' => (kind !== 'match' ? 'slots' : !home ? 'away' : comp.value === 'amical' ? 'slots' : 'hall');
    const hall = wireHallField($(f, '[data-hall-field]')!, {
      ctx, clubId: () => clubId, teamId: () => teamId,
      when: () => ({ day: date.value, start: start.value, end: end.value, weekdays: null, until: null }),
      free: () => false, setTime: (d, s, e) => { date.value = d; start.value = s; end.value = e; },
    });

    const fillSquads = () => {
      const q = squadsOf(teamId);
      squadSel.innerHTML = html`<option value="">Tout le groupe</option>${q.map(x => html`<option value="${x.id}">${x.name}</option>`)}`.s;
      $(f, '.squad-field')!.hidden = q.length < 2;
    };
    /** Les champs selon le créneau choisi : bornes, jour, « Toutes les semaines », lieu, phrase. */
    const applyChoice = (fresh: boolean) => {
      const c = choice;
      const m = mode();
      const inList = m === 'slots';
      show('[data-place]', m === 'away' || (inList && c?.kind === 'ailleurs'));
      const canWeekly = kind !== 'match' && (m !== 'slots' || (!!c && ['team', 'free', 'ailleurs', 'hall'].includes(c.kind)));
      show('[data-weekly]', canWeekly);
      if (!canWeekly) weekly.checked = false;
      show('[data-msg]', inList && c?.kind === 'free' && !b.manage && !c.pending);
      $(f, '[data-msg-label]')!.textContent = `Un mot pour ${respFirstName(b)} (facultatif)`;
      const hint = $(f, '[data-hint]')!;
      if (inList && c) {
        if (fresh) {
          date.value = nextDay(c, today, new Date());
          if (c.start && c.end) { start.value = c.start; end.value = c.end; }
          else if (!start.value) { start.value = '19:00'; end.value = '20:30'; }
          weekly.checked = canWeekly && ((c.kind === 'team' && !c.habit) || c.kind === 'free') ;
        }
        for (const x of [start, end]) { if (c.start && c.end) { x.min = c.start; x.max = c.end; } else { x.removeAttribute('min'); x.removeAttribute('max'); } }
        if (c.day) { date.min = c.day; date.max = c.dayMax ?? c.day; } else { date.min = today; date.removeAttribute('max'); }
        mount(hint, c.kind === 'ailleurs' ? html`<span class="chip chip-mute ps-off">Hors planning des salles</span> ${choiceHint(c, b)}` : html`${choiceHint(c, b)}`);
      } else {
        for (const x of [start, end, date]) { x.removeAttribute('min'); x.removeAttribute('max'); }
        date.min = today;
        if (fresh && !date.value) { date.value = today; start.value = '19:00'; end.value = '21:00'; }
        mount(hint, html``);
      }
      hint.hidden = !hint.textContent?.trim();
      const wd = inList && c?.weekday ? c.weekday : date.value ? isoDay(date.value) : null;
      $(f, '[data-weekly-txt]')!.textContent = weeklyLine(wd);
      $(f, '[data-date-label]')!.textContent = weekly.checked ? 'À partir du' : 'Date';
      const go = $<HTMLButtonElement>(f, '[data-go]')!;
      go.textContent = inList && c?.kind === 'free' && !b.manage ? `Demander à ${respFirstName(b)}` : 'Programmer';
    };
    const paintList = () => {
      list = choicesFor(b, teamId, new Date(), { match: kind === 'match' });
      if (!choice || !list.some(c => c.key === choice!.key)) choice = defaultChoice(list);
      mount(ul, slotList(list, choice?.key ?? null));
      const t = teams.find(x => x.id === teamId);
      const none = !list.some(c => c.kind === 'team');
      const noslot = $(f, '[data-noslot]')!;
      noslot.hidden = !none;
      if (none) mount(noslot, html`Pas encore de créneau pour les ${t?.name ?? 'l’équipe'} : <a href="#/salles">demande un créneau au responsable des salles</a>${list.some(c => c.kind === 'free') ? ' (un créneau libre ci-dessus, ou un autre)' : ''}.`);
      for (const r of $$<HTMLInputElement>(ul, 'input[name=slot]')) r.addEventListener('change', () => { choice = list.find(c => c.key === r.value) ?? null; applyChoice(true); });
      applyChoice(true);
    };
    const paintMode = () => {
      const m = mode();
      show('[data-title]', kind === 'other');
      $(f, '.match-fields')!.hidden = kind !== 'match';
      show('[data-slots]', m === 'slots');
      show('[data-hallsel]', m === 'hall');
      if (m === 'hall') hall.refresh();
      paintList();
    };

    fillSquads();
    paintMode();
    $<HTMLSelectElement>(f, '[name=team]')!.addEventListener('change', (e) => { teamId = (e.target as HTMLSelectElement).value; choice = null; fillSquads(); paintMode(); });
    for (const x of $$<HTMLButtonElement>(f, '[data-kind]')) x.addEventListener('click', () => {
      kind = x.dataset.kind as Kind;
      $$(f, '[data-kind]').forEach(y => y.setAttribute('aria-pressed', String(y === x)));
      paintMode();
    });
    for (const x of $$<HTMLButtonElement>(f, '[data-home]')) x.addEventListener('click', () => {
      home = x.dataset.home === '1';
      $$(f, '[data-home]').forEach(y => y.setAttribute('aria-pressed', String(y === x)));
      paintMode();
    });
    comp.addEventListener('change', paintMode);
    weekly.addEventListener('change', () => applyChoice(false));
    date.addEventListener('change', () => applyChoice(false));
    f.addEventListener('change', (e) => { if ((e.target as HTMLElement).closest('[data-hall-field], .row-3')) hall.refresh(); });

    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      msg.textContent = '';
      const d = new FormData(f);
      const m = mode();
      const day = date.value, a = start.value, z = end.value;
      const opponent = String(d.get('opponent') ?? '').trim();
      if (kind === 'match' && !opponent) { msg.textContent = 'Indique l’adversaire.'; return; }
      const place = String(d.get('place') ?? '').trim();
      const note = String(d.get('note') ?? '').trim() || null;
      const title = kind === 'match' ? opponent : kind === 'other' ? String(d.get('title') ?? '').trim() || TITLE.other : TITLE.training;
      const c = m === 'slots' ? choice : null;
      if (m === 'slots') {
        if (!c) { msg.textContent = 'Choisis un créneau, ou « Ailleurs ».'; return; }
        const bad = checkChoice(c, b, { day, start: a, end: z, weekly: weekly.checked, now: new Date(), place });
        if (bad) { msg.textContent = bad; return; }
      } else {
        if (!day || !a) { msg.textContent = 'Indique la date et l’heure.'; return; }
        if (z && z <= a) { msg.textContent = 'La fin doit être après le début.'; return; }
      }
      const squad = String(d.get('squad') ?? '') || null;
      try {
        // Un créneau libre : la demande au responsable des salles (le responsable lui-même : c'est accepté tout de suite).
        if (c?.kind === 'free') {
          const id = await askSlotFor({ slot: c.slotId ?? null, extra: null, team: teamId, weekly: kind !== 'match' && weekly.checked, day, starts: a, ends: z,
            message: [kind === 'match' ? `Match amical contre ${opponent}` : kind === 'other' ? title : null, String(d.get('msg') ?? '').trim() || null].filter(Boolean).join(' · ') || null,
            kind, title: kind === 'other' ? title : null, opponent: kind === 'match' ? opponent : null, squad: kind === 'match' ? squad : null, note });
          if (b.manage) await answerSlotAsk(id, 'accept');
          close(); toast(b.manage ? 'Créneau donné : c’est posé dans le planning de l’équipe' : `Demande envoyée à ${respFirstName(b)}`); onCreated();
          return;
        }
        const until = weekly.checked ? (await activeSeason(teams.find(t => t.id === teamId)!.club_id).catch(() => null))?.ends_on ?? seasonEnd(day) : null;
        const hallId = m === 'hall' ? hall.value().hall_id : m === 'away' ? null : c!.kind === 'ailleurs' ? null : c!.hallId;
        const row: NewEvent = {
          team_id: teamId, kind, title, starts_at: new Date(`${day}T${a}`).toISOString(), ends_at: z ? new Date(`${day}T${z}`).toISOString() : null,
          hall_id: hallId, location: m === 'hall' ? hall.value().location : hallId ? b.halls.find(x => x.id === hallId)?.name ?? null : place || null,
          note, recurrence: weekly.checked && until ? { weekdays: [isoDay(day)], until } : null,
          ...(kind !== 'match' && c?.kind === 'ailleurs' ? { off_plan: true } : {}),
        };
        const ev = await createEvent(row);
        if (kind === 'match') {
          await saveMatch({ event_id: ev.id, opponent, is_home: home, competition: (m === 'slots' ? 'amical' : comp.value) as MatchCompetition, squad_id: squad });
        }
        close(); toast(c?.kind === 'ailleurs' ? 'Programmé · hors planning des salles' : weekly.checked ? 'Séances programmées' : 'Programmé'); onCreated();
      } catch (er) { msg.textContent = failText(er); }
    });
  });
}

// =====================================================================================================================
// Modifier une séance (même règle)
// =====================================================================================================================
const hhmm = (iso: string | null | undefined) => (iso ? clock(iso) : '');

/** Modifier une séance (une fois ou la série) d'un club qui a le planning des salles ; sans planning général, `legacy`. */
export async function openSlotEventEdit(ev: ClubEvent, ctx: Ctx, onDone: () => void, legacy: () => void): Promise<void> {
  const team = ctx.teams.find(t => t.id === ev.team_id);
  if (!team) { legacy(); return; }
  const today = localDate(new Date());
  const b = await hallBoard(team.club_id, today, addDays(today, 13)).catch(() => null);
  if (!b || !hasPlan(b)) { legacy(); return; }
  const series = !!ev.recurrence?.weekdays?.length;
  const first = localDate(new Date(ev.starts_at));
  const a0 = hhmm(ev.starts_at), z0 = hhmm(ev.ends_at ?? null);
  const days0 = series ? [...ev.recurrence!.weekdays] : [isoDay(first)];
  // (« Ta séance y est déjà » : c'est elle-même, ici.)
  const all = choicesFor(b, ev.team_id, new Date()).filter(c => (series ? ['team', 'hall', 'ailleurs'] : ['team', 'freed', 'extra', 'hall', 'ailleurs']).includes(c.kind))
    .map(c => ({ ...c, habit: false }));
  // Où elle est aujourd'hui : « Ailleurs », son créneau (un seul jour), une salle sans créneaux ; sinon « Comme maintenant ».
  const inSlot = all.find(c => c.kind === 'team' && c.hallId === ev.hall_id && days0.length === 1 && c.weekday === days0[0] && c.start! <= a0 && (!z0 || c.end! >= z0));
  const cur = ev.off_plan ? all.find(c => c.kind === 'ailleurs') : inSlot ?? all.find(c => c.kind === 'hall' && c.hallId === ev.hall_id);
  const where = ev.location ?? 'lieu à préciser';
  const keep: Choice = { key: 'keep', kind: 'team', title: `Comme maintenant · ${series ? days0.map(d => WEEKDAYS[d - 1]).join(', ') : ''} ${hr(a0, z0 || null)} · ${where}`.replace(' ·  ', ' · '),
    sub: 'Ne colle à aucun créneau de l’équipe : choisis-en un, ou « Ailleurs ». Sinon, seuls l’intitulé, les infos et la fin changent.',
    hallId: ev.hall_id ?? null, weekday: null, day: null, dayMax: null, start: null, end: null };
  const list = cur ? all : [keep, ...all];
  let choice: Choice = cur ?? keep;
  const days = new Set<number>(days0);

  openSheet(series ? 'Modifier la série' : ev.kind === 'training' ? 'Modifier la séance' : 'Modifier', html`
    <form class="form" id="f-edit" novalidate data-guide="ev-slot-edit">
      <label class="field"><span>Équipe</span><select name="team_ro" disabled><option>${team.name}</option></select></label>
      <p class="ps-sub">Quel créneau</p>
      <ul class="ps-slots">${slotList(list, choice.key)}</ul>
      <label class="field ps-only" data-place hidden><span>Lieu</span><input name="place" maxlength="120" value="${ev.off_plan ? ev.location ?? '' : ''}" placeholder="ex. Parc de Rivebelle, footing"></label>
      <div class="field ps-only" data-days hidden><span>Jours</span><div class="week-chips" role="group" aria-label="Jours">
        ${DAYS.map((d, i) => html`<button type="button" data-day="${i + 1}" aria-pressed="${String(days.has(i + 1))}" aria-label="${WEEKDAYS[i]}">${d}</button>`)}</div></div>
      <div class="${series ? 'row-2' : 'row-3'}">
        ${series ? '' : html`<label class="field"><span>Date</span><input name="date" type="date" value="${first}"></label>`}
        <label class="field"><span>Début</span><input name="start" type="time" value="${a0}"></label>
        <label class="field"><span>Fin</span><input name="end" type="time" value="${z0}"></label>
      </div>
      ${series ? html`<label class="field"><span>Jusqu’au</span><input name="until" type="date" value="${ev.recurrence!.until ?? ''}"></label>` : ''}
      <p class="hint" data-hint></p>
      ${ev.kind !== 'match' ? html`<label class="field"><span>Intitulé</span><input name="title" maxlength="120" value="${ev.title}"></label>` : ''}
      <label class="field"><span>Infos pour l’équipe</span><textarea name="note" rows="2" maxlength="500">${ev.note ?? ''}</textarea></label>
      ${series ? html`<p class="hint">S’applique à toute la série. Les séances déjà annulées le restent${ev.recurrence!.except?.length ? ` (${ev.recurrence!.except.length})` : ''}.</p>` : ''}
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Enregistrer</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-edit')!;
    const msg = $(f, '.form-msg')!;
    const start = $<HTMLInputElement>(f, '[name=start]')!, end = $<HTMLInputElement>(f, '[name=end]')!;
    const date = $<HTMLInputElement>(f, '[name=date]');
    const apply = (fresh: boolean) => {
      const c = choice;
      const keepIt = c.key === 'keep';
      $(f, '[data-place]')!.hidden = c.kind !== 'ailleurs';
      $(f, '[data-days]')!.hidden = !series || !(c.kind === 'ailleurs' || c.kind === 'hall');
      for (const x of [start, end]) { x.disabled = keepIt; if (c.start && c.end) { x.min = c.start; x.max = c.end; } else { x.removeAttribute('min'); x.removeAttribute('max'); } }
      if (date) { date.disabled = keepIt; if (c.day) { date.min = c.day; date.max = c.dayMax ?? c.day; } else { date.min = today; date.removeAttribute('max'); } }
      if (fresh && !keepIt) {
        if (c.start && c.end && (start.value < c.start || start.value >= c.end)) start.value = c.start;
        if (c.start && c.end && (!end.value || end.value > c.end || end.value <= start.value)) end.value = c.end;
        if (date && (c.weekday || c.day) && (c.day ? date.value !== c.day : isoDay(date.value) !== c.weekday)) date.value = nextDay(c, today, new Date());
      }
      const hint = $(f, '[data-hint]')!;
      mount(hint, keepIt ? html`` : c.kind === 'ailleurs' ? html`<span class="chip chip-mute ps-off">Hors planning des salles</span> ${choiceHint(c, b)}` : html`${choiceHint(c, b)}`);
      hint.hidden = !hint.textContent?.trim();
    };
    for (const r of $$<HTMLInputElement>(f, 'input[name=slot]')) r.addEventListener('change', () => { choice = list.find(c => c.key === r.value) ?? keep; apply(true); });
    for (const x of $$<HTMLButtonElement>(f, '[data-day]')) x.addEventListener('click', () => {
      const d = Number(x.dataset.day);
      if (days.has(d)) days.delete(d); else days.add(d);
      x.setAttribute('aria-pressed', String(days.has(d)));
    });
    apply(false);

    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      msg.textContent = '';
      const d = new FormData(f);
      const c = choice;
      const until = String(d.get('until') ?? '');
      if (series && (!until || until < first)) { msg.textContent = 'Choisis une date de fin après la première séance.'; return; }
      const base = { ...(ev.kind !== 'match' ? { title: String(d.get('title') ?? '').trim() || ev.title } : {}), note: String(d.get('note') ?? '').trim() || null };
      try {
        if (c.key === 'keep') {
          await updateEvent(ev.id, { ...base, ...(series ? { recurrence: { ...ev.recurrence, until } } : {}) });
        } else {
          const day = series ? first : date!.value;
          const a = start.value, z = end.value;
          const wd = c.weekday ? [c.weekday] : [...days].sort();
          if (series && !wd.length) { msg.textContent = 'Choisis au moins un jour.'; return; }
          if (!a || !z || z <= a) { msg.textContent = 'La fin doit être après le début.'; return; }
          if (c.start && c.end && (a < c.start || z > c.end)) { msg.textContent = `Dans le créneau, s’il te plaît : ${hr(c.start, c.end)} au plus.`; return; }
          if (c.kind === 'ailleurs' && !String(d.get('place') ?? '').trim()) { msg.textContent = 'Indique le lieu.'; return; }
          const bad = series ? null : checkChoice(c, b, { day, start: a, end: z, weekly: false, now: new Date(), place: String(d.get('place') ?? '') });
          if (bad) { msg.textContent = bad; return; }
          const off = c.kind === 'ailleurs';
          await updateEvent(ev.id, {
            ...base, starts_at: new Date(`${day}T${a}`).toISOString(), ends_at: z ? new Date(`${day}T${z}`).toISOString() : null,
            hall_id: off ? null : c.hallId, location: off ? String(d.get('place') ?? '').trim() || null : b.halls.find(x => x.id === c.hallId)?.name ?? null,
            off_plan: off,
            ...(series ? { recurrence: { ...ev.recurrence, weekdays: wd, until } } : {}),
          });
        }
        close(); toast(series ? 'Série modifiée' : 'Modifié'); onDone();
      } catch (er) { msg.textContent = failText(er); }
    });
  });
}
